import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { RootState } from "../store";
import SplashScreen from "../components/SplashScreen";
import { JSX } from "react";

type UserRole = "admin" | "analyst" | "approver" | "user";

interface ProtectedRouteProps {
  children: JSX.Element;
  role: "admin" | "user" | "analyst" | "approver";
}

export default function ProtectedRoute({ children, role }: ProtectedRouteProps) {
  const { user, loading, loadedFromFirestore, roleConfirmed } = useSelector(
    (state: RootState) => state.auth
  );
  const location = useLocation();

  // 1️⃣ Still loading while signed in → show splash
  if (loading || (!user && loading)) {
    return <SplashScreen />;
  }

  // 2️⃣ If NO USER → send directly to login
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // 3️⃣ Wait for Firestore + role confirm only when logged-in
  if (!loadedFromFirestore || !roleConfirmed) {
    return <SplashScreen />;
  }

  const userRole = user.role as UserRole;

  // 4️⃣ Admin routes (admin, analyst, approver allowed)
  if (role === "admin") {
    if (
      userRole !== "admin" &&
      userRole !== "analyst" &&
      userRole !== "approver"
    ) {
      return <Navigate to="/" replace />;
    }
  }

  // 5️⃣ User-only routes (admins/analysts go to admin)
  if (role === "user") {
    if (userRole !== "user") {
      return <Navigate to="/admin" replace />;
    }
  }

  // 🔵 **USER-SPECIFIC FLOW**
  // 🔵 USER-SPECIFIC LOGIC
if (userRole === "user") {

  // 1) Force setup-profile if not completed
  if (!user.basicInfoSetup && location.pathname !== "/setup-profile") {
    return <Navigate to="/setup-profile" replace />;
  }

  // Prevent entering setup-profile again after completion
  if (user.basicInfoSetup && location.pathname === "/setup-profile") {
    if (user.hasReports) {
      return <Navigate to="/" replace />;
    } else {
      return <Navigate to="/onboard" replace />;
    }
  }

  // 2) After setup-profile is completed → handle home logic
  if (user.basicInfoSetup) {
    
    // ❗ ALLOW PROFILE ROUTES ALWAYS
    const allowedUserRoutes = [
      "/user/profile",
    ];
    const isProfileRoute =
      location.pathname.startsWith("/user/profile");

    if (isProfileRoute) {
      return children; // allow access
    }

    // ❗ For HOME ROUTE "/"
    if (location.pathname === "/") {
      if (user.hasReports) {
        return children; // homepage shows reports
      } else {
        return <Navigate to="/onboard" replace />;
      }
    }

    // ❗ For ONBOARD page
    if (location.pathname === "/onboard") {
      if (user.hasReports) {
        return <Navigate to="/" replace />;
      } else {
        return children;
      }
    }
  }
}


  // 8️⃣ All checks passed
  return children;
}



// import { useSelector } from "react-redux";
// import { Navigate, useLocation } from "react-router-dom";
// import { RootState } from "../store";
// import { JSX } from "react";
// import SplashScreen from "../components/SplashScreen";

// /** All roles a user can have */
// type UserRole = "admin" | "analyst" | "approver" | "user";

// /**
//  * The role required by the route:
//  * - "admin"  → allow admin, analyst, approver
//  * - "user"   → allow only user
//  */
// type RouteRole = "admin" | "user";

// interface ProtectedRouteProps {
//   children: JSX.Element;
//   role: RouteRole;
// }

// export default function ProtectedRoute({ children, role }: ProtectedRouteProps) {
//   const { user, loading, loadedFromFirestore, roleConfirmed } = useSelector(
//     (state: RootState) => state.auth
//   );
//   const location = useLocation();

//   // 1️⃣ While Firebase or Firestore loading → show splash
//   if (loading || !loadedFromFirestore || !roleConfirmed) {
//     return <SplashScreen />;
//   }

//   // 2️⃣ Not logged in → redirect to login
//   if (!user) {
//     return <Navigate to="/auth/login" state={{ from: location }} replace />;
//   }

//   const userRole = user.role as UserRole;

//   // 3️⃣ Role-based access control
//   if (role === "admin") {
//     // Admin route: allow admin, analyst, approver
//     if (userRole !== "admin" && userRole !== "analyst" && userRole !== "approver") {
//       return <Navigate to="/" replace />;
//     }
//   }

//   if (role === "user") {
//     // User route: allow ONLY role "user"
//     if (userRole !== "user") {
//       return <Navigate to="/admin" replace />;
//     }
//   }

//   // 4️⃣ Force setup-profile ONLY for "user"
//   if (
//     userRole === "user" &&
//     user.basicInfoSetup === false &&
//     location.pathname !== "/setup-profile"
//   ) {
//     return <Navigate to="/setup-profile" replace />;
//   }

//   // 5️⃣ Don't allow user to re-enter setup-profile if already completed
//   if (
//     userRole === "user" &&
//     user.basicInfoSetup === true &&
//     location.pathname === "/setup-profile"
//   ) {
//     return <Navigate to="/" replace />;
//   }

//   // 6️⃣ All checks passed → allow route to render
//   return children;
// }
